import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useWorldsStore } from '../worlds.store';
import { hexToRgb } from '../constants';
import type { Country, GeoRegion, Level } from '../worlds.types';
import { ChunkGridLayer, CellPreviewLayer, GridLinesLayer, packRgba, type ChunkPaint } from './ChunkGridLayer';
import { chunkRectBounds, latLngToBlock, latLngToChunk } from './coords';
import { buildRegionLabels, regionLabels } from './overlays';
import { screenPxPerImagePx, staticBackgroundBounds, staticBackgroundFor, type StaticBackground } from './staticBackgrounds';

// ============================================================
// Carte Leaflet partagée par les onglets Carte et Marqueurs
// ============================================================
// Un seul hook : fond (tuiles RecubeDynmap si le niveau a une tileUrl ; sinon image fixe du
// terrain s'il en existe une pour ce niveau, voir staticBackgrounds.ts ; sinon fond neutre et
// quadrillage), couche des chunks, couche des régions (contour de 3 px à tous les zooms, libellés
// au centroïde des chunks), grille des chunks optionnelle, aperçu du pinceau, coordonnées sous la
// souris, plein écran CSS et menu du clic droit. Les onglets posent leurs propres surcouches
// (centres, marqueurs) sur `map`.
// ============================================================

export interface WorldMapOptions {
  showChunks: boolean;
  showBorders: boolean;
  showRegions: boolean;
  /** Opacité des pays (l'onglet Marqueurs les atténue). */
  chunkAlpha?: number;
  /** Classe CSS des libellés de région (module SCSS de la page). */
  labelClass: string;
  /** Classe de la pastille de couleur en tête des libellés de région (absente : pas de pastille). */
  labelDotClass?: string;
  /** Grille des chunks au-dessus des tuiles et des chunks peints (défaut : non). */
  showGridLines?: boolean;
  /** Image fixe du terrain sous les pays, quand le niveau n'a pas de tuiles (défaut : oui). */
  showBackground?: boolean;
}

/**
 * Image fixe du niveau : masquée (couche éteinte), en chargement, affichée, ou introuvable (retour
 * au fond neutre).
 */
export type StaticBgState = 'hidden' | 'loading' | 'ok' | 'error';

export interface MapMenu {
  left: number;
  top: number;
  x: number;
  z: number;
}

export interface WorldMap {
  containerRef: React.RefObject<HTMLDivElement | null>;
  coordsRef: React.RefObject<HTMLSpanElement | null>;
  map: L.Map | null;
  preview: CellPreviewLayer | null;
  level: Level | null;
  fullscreen: boolean;
  setFullscreen: (v: boolean) => void;
  menu: MapMenu | null;
  closeMenu: () => void;
  /** Centre la vue sur un bloc (zoom 5 par défaut : 32 px par chunk). */
  focusBlock: (x: number, z: number, zoom?: number) => void;
  /** Cadre la vue sur une emprise de chunks. */
  focusChunks: (b: { minX: number; maxX: number; minZ: number; maxZ: number }) => void;
  /** Zoom courant (2^zoom px par chunk). */
  zoom: number;
  /** Tuiles en erreur depuis le dernier changement de tileUrl (hors tuile vide de remplacement). */
  tileErrors: number;
  /** Tuiles réellement chargées depuis le dernier changement de tileUrl. */
  tileLoads: number;
  /** Image fixe du niveau affiché (niveau sans tileUrl qui en a une), sinon null. */
  staticBg: StaticBackground | null;
  /** État de `staticBg` (sans objet quand elle est null). */
  staticBgState: StaticBgState;
}

/** Tuile d'erreur transparente : pas d'icône d'image cassée sur les zones sans rendu. */
const EMPTY_TILE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Image fixe : rendu pixelisé dès qu'un pixel de l'image couvre au moins 4 px écran (zoom 4 pour
 * 6 blocs par pixel), lissé en dessous.
 */
const BG_PIXELATED_FROM = 4;

/**
 * Éléments <img> des images fixes, par URL, partagés par les montages successifs de la carte (une
 * seule carte à la fois : onglet Carte ou Marqueurs). L'élément tient l'image chargée : revenir
 * sur l'onglet, rallumer la couche ou revenir sur le niveau ne relance pas de requête (pas même
 * une revalidation 304), y compris après un passage du ramasse-miettes. Créé au premier
 * affichage, jamais préchargé.
 */
const bgImages = new Map<string, HTMLImageElement>();

function bgImageReady(img: HTMLImageElement | undefined): boolean {
  return !!img && img.complete && img.naturalWidth > 0;
}

function bgImage(url: string): HTMLImageElement {
  let img = bgImages.get(url);
  // Échec précédent (chargement terminé sans pixels) : nouvel élément, nouvel essai.
  if (img && img.complete && img.naturalWidth === 0) img = undefined;
  if (!img) {
    img = document.createElement('img');
    // Décodage hors du fil principal (4032 x 2848 px) : pas d'à-coup au premier affichage.
    img.decoding = 'async';
    img.alt = '';
    img.src = url;
    bgImages.set(url, img);
  }
  return img;
}

/** @types/leaflet ne déclare que l'URL ; Leaflet 1.9 accepte aussi un <img> déjà créé (_initImage). */
const imageOverlayOf = L.imageOverlay as unknown as (
  img: HTMLImageElement, bounds: L.LatLngBoundsExpression, options?: L.ImageOverlayOptions,
) => L.ImageOverlay;

function countryPaint(countries: Country[], alpha: number): ChunkPaint {
  const group = new Uint16Array(65536);
  const fill = new Uint32Array(65536);
  for (const c of countries) {
    if (c.idx <= 0 || c.idx > 65535) continue;
    const [r, g, b] = hexToRgb(c.color);
    group[c.idx] = c.idx;
    fill[c.idx] = packRgba(r, g, b, Math.round(255 * (c.enabled ? alpha : alpha * 0.45)));
  }
  return { group, fill, stroke: { width: 1, color: 'rgba(8, 12, 22, 0.8)' } };
}

function regionPaint(countries: Country[], regions: GeoRegion[]): ChunkPaint {
  const group = new Uint16Array(65536);
  const fill = new Uint32Array(regions.length + 1);
  const byGroup: string[] = [''];
  const ord = new Map<string, number>();
  regions.forEach((r, i) => {
    ord.set(r.id, i + 1);
    const [cr, cg, cb] = hexToRgb(r.color);
    fill[i + 1] = packRgba(cr, cg, cb, 0x33);
    byGroup.push(r.color);
  });
  for (const c of countries) {
    const o = c.geoRegionId ? ord.get(c.geoRegionId) : undefined;
    if (o && c.idx > 0 && c.idx <= 65535) group[c.idx] = o;
  }
  // Contour de 3 px à tous les zooms, comme RegionContoursLayer de NG (le panel s'arrêtait au zoom 2).
  return { group, fill, stroke: { width: 3, minZoom: 0, byGroup } };
}

export function useWorldMap(opts: WorldMapOptions): WorldMap {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const coordsRef = useRef<HTMLSpanElement | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const [fullscreen, setFullscreenState] = useState(false);
  const [menu, setMenu] = useState<MapMenu | null>(null);
  const [zoom, setZoom] = useState(0);
  const [tileStats, setTileStats] = useState({ errors: 0, loads: 0 });
  const [bgStatus, setBgStatus] = useState<'loading' | 'ok' | 'error'>('loading');

  const layersRef = useRef<{
    chunks: ChunkGridLayer; regions: ChunkGridLayer; lines: GridLinesLayer; gridLines: GridLinesLayer;
    preview: CellPreviewLayer; labels: L.LayerGroup;
  } | null>(null);
  const tilesRef = useRef<L.TileLayer | null>(null);
  const fittedRef = useRef<string | null>(null);

  const levelName = useWorldsStore((s) => s.level);
  const levels = useWorldsStore((s) => s.levels);
  const countries = useWorldsStore((s) => s.countries);
  const geoRegions = useWorldsStore((s) => s.geoRegions);
  const grid = useWorldsStore((s) => s.grid);
  const gridRev = useWorldsStore((s) => s.gridRev);
  const gridChange = useWorldsStore((s) => s.gridChange);
  const level = useMemo(() => levels.find((l) => l.name === levelName) ?? null, [levels, levelName]);

  // ---- Création / destruction de la carte ----
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const m = L.map(el, {
      crs: L.CRS.Simple,
      minZoom: -2,
      maxZoom: 8,
      zoomSnap: 1,
      attributionControl: false,
      // Double-clic réservé aux outils (le zoom reste à la molette et aux boutons).
      doubleClickZoom: false,
      boxZoom: false,
    });
    // Image fixe du terrain : sous les tuiles (200), donc sous tout le reste.
    m.createPane('worldsBackground').style.zIndex = '150';
    m.createPane('worldsChunks').style.zIndex = '350';
    m.createPane('worldsGrid').style.zIndex = '355';
    m.createPane('worldsRegions').style.zIndex = '360';
    m.createPane('worldsPreview').style.zIndex = '450';
    m.setView([0, 0], 0);

    const chunks = new ChunkGridLayer(countryPaint([], 0.55), { pane: 'worldsChunks' });
    const regions = new ChunkGridLayer(regionPaint([], []), { pane: 'worldsRegions' });
    const lines = new GridLinesLayer({ zIndex: 2 });
    const gridLines = new GridLinesLayer({ pane: 'worldsGrid', variant: 'overlay' });
    const preview = new CellPreviewLayer({ pane: 'worldsPreview' });
    const labels = L.layerGroup();
    preview.addTo(m);
    layersRef.current = { chunks, regions, lines, gridLines, preview, labels };

    m.on('mousemove', (e: L.LeafletMouseEvent) => {
      const el2 = coordsRef.current;
      if (!el2) return;
      const { x, z } = latLngToBlock(e.latlng);
      const { cx, cz } = latLngToChunk(e.latlng);
      el2.textContent = `X ${x} · Z ${z} · chunk ${cx}, ${cz}`;
    });
    // Pointeur hors de la carte : champ vidé, la feuille affiche l'invite « Survolez la carte »
    // (.coords:empty).
    m.on('mouseout', () => { if (coordsRef.current) coordsRef.current.textContent = ''; });
    m.on('contextmenu', (e: L.LeafletMouseEvent) => {
      const { x, z } = latLngToBlock(e.latlng);
      setMenu({ left: e.containerPoint.x, top: e.containerPoint.y, x, z });
    });
    const close = () => setMenu(null);
    m.on('movestart zoomstart mousedown', close);
    m.on('zoomend', () => setZoom(m.getZoom()));
    setZoom(m.getZoom());
    setMap(m);

    return () => {
      layersRef.current = null;
      tilesRef.current = null;
      fittedRef.current = null;
      m.remove();
      setMap(null);
    };
  }, []);

  // ---- Fond : tuiles RecubeDynmap, image fixe ou quadrillage ----
  const tileUrl = level?.tileUrl ?? null;
  const tileMaxZoom = level?.tileMaxZoom ?? 8;
  useEffect(() => {
    const L2 = layersRef.current;
    if (!map || !L2) return;
    if (tilesRef.current) { tilesRef.current.remove(); tilesRef.current = null; }
    // Compteurs remis à zéro à chaque URL : ils disent si CETTE URL sert des tuiles. Les mises à
    // jour de l'état sont regroupées (une tuile en erreur ne relance pas un rendu de l'onglet).
    const counts = { errors: 0, loads: 0 };
    let flush: number | null = null;
    let dead = false;
    const bump = () => {
      if (dead || flush != null) return;
      flush = window.setTimeout(() => { flush = null; setTileStats({ ...counts }); }, 400);
    };
    setTileStats((p) => (p.errors || p.loads ? { errors: 0, loads: 0 } : p));
    if (tileUrl) {
      const minNative = Math.max(0, 8 - tileMaxZoom);
      const tiles = L.tileLayer(tileUrl, {
        tileSize: 256,
        maxZoom: 8,
        maxNativeZoom: 8,
        minNativeZoom: minNative,
        // Sous le zoom natif le plus large, Leaflet réduirait des milliers de tuiles : on s'arrête.
        minZoom: minNative,
        zoomReverse: true,
        noWrap: true,
        keepBuffer: 1,
        zIndex: 1,
        errorTileUrl: EMPTY_TILE,
      });
      // Une tuile en erreur est remplacée par EMPTY_TILE, dont le chargement émet aussi tileload :
      // on ne compte que les vraies tuiles.
      tiles.on('tileerror', () => { counts.errors++; bump(); });
      tiles.on('tileload', (e: L.TileEvent) => {
        if ((e.tile as HTMLImageElement).getAttribute('src') === EMPTY_TILE) return;
        counts.loads++;
        bump();
      });
      tilesRef.current = tiles.addTo(map);
    }
    return () => {
      dead = true;
      if (flush != null) window.clearTimeout(flush);
    };
  }, [map, tileUrl, tileMaxZoom]);

  // Image fixe : seulement pour un niveau chargé sans tileUrl (objet stable, pris dans la table).
  // Posée à l'affichage de la carte (l'onglet monte la carte), jamais préchargée. Un nouvel
  // L.imageOverlay à chaque pose, mais toujours le même <img> (bgImage) : Leaflet ne réémet pas
  // « load » pour une image déjà chargée, d'où l'état « ok » posé tout de suite dans ce cas.
  const staticBg = staticBackgroundFor(level);
  const wantBg = !!staticBg && opts.showBackground !== false;
  useEffect(() => {
    if (!map || !staticBg || !wantBg) return;
    const img = bgImage(staticBg.url);
    setBgStatus(bgImageReady(img) ? 'ok' : 'loading');
    let dead = false;
    const overlay = imageOverlayOf(img, staticBackgroundBounds(staticBg), {
      pane: 'worldsBackground',
      interactive: false,
      opacity: 1,
    });
    overlay.on('load', () => { if (!dead) setBgStatus('ok'); });
    // Introuvable : image retirée, retour au fond neutre (quadrillage), note dans MapFrame. Le
    // prochain montage recrée l'élément et réessaie.
    overlay.on('error', () => {
      if (dead) return;
      overlay.remove();
      setBgStatus('error');
    });
    const setRendering = () => {
      img.style.imageRendering = screenPxPerImagePx(staticBg, map.getZoom()) >= BG_PIXELATED_FROM ? 'pixelated' : '';
    };
    overlay.addTo(map);
    setRendering();
    map.on('zoomend', setRendering);
    return () => {
      dead = true;
      map.off('zoomend', setRendering);
      overlay.remove();
    };
  }, [map, staticBg, wantBg]);

  // État exposé : une image déjà chargée par un montage précédent compte comme affichée dès le
  // premier rendu (ni quadrillage ni pastille « chargement » le temps que l'effet le confirme).
  const bgState: StaticBgState = !staticBg || !wantBg
    ? 'hidden'
    : bgStatus === 'loading' && bgImageReady(bgImages.get(staticBg.url)) ? 'ok' : bgStatus;

  // Quadrillage du fond neutre : ni sous des tuiles, ni sous l'image fixe une fois chargée. Il
  // reste pendant le téléchargement (la carte n'est pas vide) et revient si l'image manque.
  const bgShown = bgState === 'ok';
  useEffect(() => {
    const L2 = layersRef.current;
    if (!map || !L2) return;
    if (tileUrl || bgShown) L2.lines.remove(); else L2.lines.addTo(map);
  }, [map, tileUrl, bgShown]);

  // ---- Grille des chunks (au-dessus des tuiles et des chunks peints) ----
  useEffect(() => {
    const L2 = layersRef.current;
    if (!map || !L2) return;
    if (opts.showGridLines) L2.gridLines.addTo(map); else L2.gridLines.remove();
  }, [map, opts.showGridLines]);

  // ---- Couleurs : un redessin complet seulement si couleur, activation ou région changent ----
  const alpha = opts.chunkAlpha ?? 0.55;
  const colorSig = useMemo(() => countries.map((c) => `${c.idx}:${c.color}:${c.enabled ? 1 : 0}`).join(','), [countries]);
  const regionSig = useMemo(
    () => countries.map((c) => `${c.idx}:${c.geoRegionId ?? ''}`).join(',') + '|' + geoRegions.map((r) => `${r.id}:${r.color}`).join(','),
    [countries, geoRegions],
  );
  useEffect(() => {
    layersRef.current?.chunks.setPaint(countryPaint(useWorldsStore.getState().countries, alpha));
  }, [colorSig, alpha, map]);
  useEffect(() => {
    const s = useWorldsStore.getState();
    layersRef.current?.regions.setPaint(regionPaint(s.countries, s.geoRegions));
  }, [regionSig, map]);

  // ---- Visibilité des couches ----
  useEffect(() => {
    const L2 = layersRef.current;
    if (!map || !L2) return;
    if (opts.showChunks) L2.chunks.addTo(map); else L2.chunks.remove();
  }, [map, opts.showChunks]);
  useEffect(() => { layersRef.current?.chunks.setBorders(opts.showBorders); }, [map, opts.showBorders]);
  useEffect(() => {
    const L2 = layersRef.current;
    if (!map || !L2) return;
    if (opts.showRegions) { L2.regions.addTo(map); L2.labels.addTo(map); } else { L2.regions.remove(); L2.labels.remove(); }
  }, [map, opts.showRegions]);

  // ---- Libellés de régions (centroïdes de la grille locale, recalculés au zoom) ----
  // Calculés seulement couche allumée : une passe sur la grille à chaque rattrapage (gridRev, la
  // grille étant modifiée en place).
  const labelData = useMemo(
    () => (opts.showRegions ? regionLabels(geoRegions, countries, grid?.loaded ? grid.centroids() : null) : []),
    [geoRegions, countries, grid, gridRev, opts.showRegions],
  );
  useEffect(() => {
    const L2 = layersRef.current;
    if (!L2) return;
    buildRegionLabels(L2.labels, labelData, opts.labelClass, zoom, opts.labelDotClass);
  }, [labelData, zoom, opts.labelClass, opts.labelDotClass, map]);

  // ---- Grille : nouvelle instance (changement de niveau) ou blocs modifiés ----
  useEffect(() => {
    const L2 = layersRef.current;
    if (!L2) return;
    L2.chunks.setGrid(grid);
    L2.regions.setGrid(grid);
  }, [grid, map]);
  useEffect(() => {
    const L2 = layersRef.current;
    if (!L2 || !gridChange) return;
    if (gridChange.full) { L2.chunks.repaintAll(); L2.regions.repaintAll(); }
    else { L2.chunks.repaintBlocks(gridChange.keys); L2.regions.repaintBlocks(gridChange.keys); }
  }, [gridRev, gridChange]);

  // ---- Vue initiale : l'emprise de la grille, une fois par niveau ----
  useEffect(() => {
    if (!map || !grid || !grid.loaded || fittedRef.current === grid.level) return;
    fittedRef.current = grid.level;
    const b = grid.bbox();
    if (b) map.fitBounds(chunkRectBounds(b.minX, b.maxX, b.minZ, b.maxZ), { maxZoom: 4, animate: false });
    else map.setView([0, 0], 0, { animate: false });
  }, [map, grid, gridRev]);

  // ---- Plein écran CSS (les toasts restent visibles) ----
  const setFullscreen = useCallback((v: boolean) => setFullscreenState(v), []);
  useEffect(() => {
    if (!map) return;
    const prev = document.body.style.overflow;
    if (fullscreen) document.body.style.overflow = 'hidden';
    const kick = () => map.invalidateSize();
    const r1 = requestAnimationFrame(() => { kick(); requestAnimationFrame(kick); });
    const t = window.setTimeout(kick, 250);
    return () => {
      document.body.style.overflow = prev;
      cancelAnimationFrame(r1);
      window.clearTimeout(t);
    };
  }, [fullscreen, map]);

  const closeMenu = useCallback(() => setMenu(null), []);

  const focusBlock = useCallback((x: number, z: number, zm = 5) => {
    map?.setView([-z / 16, x / 16], zm);
  }, [map]);

  const focusChunks = useCallback((b: { minX: number; maxX: number; minZ: number; maxZ: number }) => {
    map?.fitBounds(chunkRectBounds(b.minX, b.maxX, b.minZ, b.maxZ), { maxZoom: 6, padding: [24, 24] });
  }, [map]);

  return {
    containerRef,
    coordsRef,
    map,
    preview: map ? layersRef.current?.preview ?? null : null,
    level,
    fullscreen,
    setFullscreen,
    menu,
    closeMenu,
    focusBlock,
    focusChunks,
    zoom,
    tileErrors: tileStats.errors,
    tileLoads: tileStats.loads,
    staticBg,
    staticBgState: bgState,
  };
}
